import React, { memo } from "react";
import {
  View,
  TextInput as RNTextInput,
  type TextInputProps as RNTextInputProps,
} from "react-native";
import { AppText } from "@/components/ui/AppText";
import { CountryDialCodePicker } from "@/components/ui/CountryDialCodePicker";
import { Label } from "@/components/ui/Label";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { cn } from "@/lib/utils";

export interface CountryOption {
  /** Emoji flag, e.g. "🇸🇦" */
  flag: string;
  /** Dial code including "+", e.g. "+966" */
  dialCode: string;
  /** Display name, e.g. "Saudi Arabia" */
  name: string;
}

export interface PhoneInputProps extends Omit<RNTextInputProps, "style" | "placeholder"> {
  label: TranslationKeys;
  hint?: TranslationKeys;
  error?: TranslationKeys;
  /** Placeholder text — pass a translation key; the component translates it internally. */
  placeholder?: TranslationKeys;
  isRTL?: boolean;
  /**
   * The dial code displayed in the country picker (e.g. "+966").
   * @default "+966"
   */
  dialCode?: string;
  /**
   * Emoji flag for the selected country (e.g. "🇸🇦").
   * @default "🇸🇦"
   */
  countryFlag?: string;
  /**
   * Called when the user selects a country from the country picker sheet.
   */
  onCountrySelect?: (country: CountryOption) => void;
  accessibilityLabel?: TranslationKeys;
  accessibilityHint?: TranslationKeys;
  testID?: string;
  /** When true, renders a red asterisk after the label. */
  required?: boolean;
}

/**
 * Phone number input with an inline country-code picker.
 *
 * Mirrors the Figma "Input Field / Phone Number" variant (node 10007:270841).
 *
 * Layout:
 * ```
 * ┌────────────┐  ┌───────────────────────────────┐
 * │ 🇸🇦  +966  ›  │  Phone number field             │
 * └────────────┘  └───────────────────────────────┘
 * ```
 *
 * The left picker is a `CountryDialCodePicker` button — tapping it opens a
 * `CountryDialCodePickerSheet` with search. `onCountrySelect` is called with the
 * chosen country so the parent can update its `dialCode` / `countryFlag` state.
 * The right input is a standard numeric keyboard text field.
 *
 * Design tokens:
 *   - Picker bg:  `bg-stroke-40` (#E9ECF1)
 *   - Input border: `border-stroke-50` (#E4E7ED)
 *   - Both: `rounded-md`
 *
 * @example
 * <PhoneInput
 *   label="vendor.form.phone"
 *   dialCode={dialCode}
 *   countryFlag={flag}
 *   onCountrySelect={(c) => { setDialCode(c.dialCode); setFlag(c.flag); }}
 *   value={phone}
 *   onChangeText={setPhone}
 *   keyboardType="phone-pad"
 * />
 */
export const PhoneInput = memo(function PhoneInput({
  label,
  hint,
  error,
  isRTL,
  editable = true,
  dialCode = "+966",
  countryFlag = "🇸🇦",
  onCountrySelect,
  placeholder,
  accessibilityLabel,
  accessibilityHint,
  testID,
  required,
  ...rest
}: PhoneInputProps) {
  const { t, isRTL: contextIsRTL } = useI18n();
  const resolvedIsRTL = isRTL ?? contextIsRTL;
  const a11yLabel = t(accessibilityLabel ?? label);
  const resolvedPlaceholder = placeholder ? t(placeholder) : undefined;

  const inputBorderClass = error
    ? "border-error"
    : editable
      ? "border-stroke-50"
      : "border-stroke-20";
  const bgClass = editable ? "bg-white" : "bg-stroke-20";
  const textClass = editable ? "text-dark-text-50" : "text-dark-text-15";

  return (
    <View className="mb-4 w-full items-start">
      <Label label={label} required={required} />

      <View className={`flex-row gap-3 w-full`}>
        {/* ── Country picker ─────────────────────────────────────────────── */}
        <View>
          <CountryDialCodePicker
            dialCode={dialCode}
            countryFlag={countryFlag}
            editable={editable}
            onCountrySelect={onCountrySelect}
            testID={testID ? `${testID}-country-picker` : undefined}
          />
        </View>

        {/* ── Phone number input ─────────────────────────────────────────── */}
        <View className="flex-1">
          <View
            className={`flex-1 flex-row items-center border rounded-md ${bgClass} ${inputBorderClass}`}
          >
            <RNTextInput
              keyboardType="phone-pad"
              editable={editable}
              textAlign={resolvedIsRTL ? "right" : "left"}
              testID={testID}
              placeholder={resolvedPlaceholder}
              accessibilityLabel={a11yLabel}
              accessibilityHint={accessibilityHint ? t(accessibilityHint) : undefined}
              className={cn(
                "flex-1 py-3 px-4 text-base",
                resolvedIsRTL ? "text-end" : "text-start",
                textClass
              )}
              {...rest}
            />
          </View>
          {!!error && (
            <AppText role="alert" className="text-xs text-error mt-1 text-start">
              {t(error)}
            </AppText>
          )}
        </View>
      </View>

      {!!hint && !error && (
        <AppText className="text-xs text-dark-text-10 mt-1 text-start">{t(hint)}</AppText>
      )}
    </View>
  );
});
